import React, { useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "hooks"
import {
	filteredListBySearch,
	getLocationsByBranchId,
	isLocationLoading,
	setAllLocationsByBranchLoadedState,
} from "store/Location/locationDetails.slice"
import GlobalSidebar from "./GlobalSidebar"
import SidebarList from "./SidebarList"
import { getBranches, selectBranchIsLoading, selectBranchList } from "store/Branches/branch.slice"
import { isRouteDashboard } from "config/utils"
import { getCustomers, isCustomerLoading, selectCustomerOptions } from "store/Customers/customers.slice"
import { useLocation } from "react-router-dom"
import { GetLocationDetailsInterface } from "@type/locations.types"
import AntdSwitch from "./AntdSwitch"
import AntdSelect1 from "./AntdSelect"
import { Divider } from "antd-v5"
import { CaretDownOutlined, SearchOutlined } from "@ant-design/icons"
import AntdInput from "./AntdInput"
import useSchedulesQueryParams from "hooks/useSchedulesQueryParams"

interface Props {
	selected: string
	handleSelected(locationId: string): void
	handleSelectedLocations?: (location: string[]) => void
	forAllLocations?: boolean
	allowSelectAllBranches?: boolean
	isAllBranchesSelected?: boolean
	handleIsAllBranchesSelected?: () => void
	handleIsActiveOnlySelected?: (isActiveOnlySelected: boolean) => void
}

const LocationSidebar: React.FC<Props> = ({
	selected,
	handleSelected,
	forAllLocations = false,
	allowSelectAllBranches = false,
	handleSelectedLocations = () => undefined,
	isAllBranchesSelected = false,
	handleIsAllBranchesSelected = () => undefined,
	handleIsActiveOnlySelected = () => undefined,
}) => {
	const dispatch = useAppDispatch()
	const { pathname } = useLocation()
	const {
		selectedLocationBranch: paramLocationBranch = "",
		selectedCustomer: paramCustomer = "",
		activeOnly = "true",
		selectLocationBranch, // Actions
		selectCustomer,
		selectLocation,
		selectActiveOnly
	} = useSchedulesQueryParams()


	const shouldShowActiveOnly = activeOnly === "true"

	// const [isFocused, setIsFocused] = useState<boolean>(false)
	const [searchString, setSearchString] = useState("")
	const [searchOnFields] = useState(["shortName", "name"])

	//const [shouldShowActiveOnly, setShouldShowActiveOnly] = useState(true)

	const branches = useAppSelector(selectBranchList())
	const customers = useAppSelector(selectCustomerOptions)
	const filteredLocationSelector = useMemo(
		() =>
			filteredListBySearch(
				searchString,
				paramLocationBranch,
				searchOnFields,
				paramCustomer,
				shouldShowActiveOnly,
			),
		[searchString, paramLocationBranch, paramCustomer, shouldShowActiveOnly],
	)

	const filteredLocationList = useAppSelector(filteredLocationSelector)
	const loading = useAppSelector(isLocationLoading())
	const loadingCustomers = useAppSelector(isCustomerLoading())
	const loadingBranches = useAppSelector(selectBranchIsLoading())

	const isDashboardRoute = useMemo(() => isRouteDashboard(pathname), [pathname])

	useEffect(() => {
		dispatch(getCustomers())
		dispatch(getBranches(false))
	}, [])

	useEffect(() => {
		if (paramLocationBranch) {
			dispatch(getLocationsByBranchId(paramLocationBranch, shouldShowActiveOnly))
		}
	}, [paramLocationBranch, shouldShowActiveOnly]) // Added shouldShowActiveOnly dependency to ensure refresh

	// Removed the big useEffect for defaults here.
	// It is now handled by useLocationScheduleDefaults in Location.tsx
	useEffect(() => {
		if (!loading && selected) {
			document.getElementById(selected)?.scrollIntoView()
		}
	}, [loading])

	useEffect(() => {
		if (forAllLocations)
			handleSelectedLocations?.(filteredLocationList.map((item: GetLocationDetailsInterface) => item._id))
	}, [filteredLocationList.length])

	useEffect(() => {
		if (forAllLocations || isAllBranchesSelected) {
			selectLocation("") // Clear location via semantic action
			handleSelectedLocations?.(filteredLocationList.map(item => item._id))
		} else if (isDashboardRoute) {
			if (!selected) {
				const activeLocation = filteredLocationList.find(item => item.isActive)?._id ?? ""
				selectLocation(activeLocation)
			}

			handleSelectedLocations([])
		}
	}, [forAllLocations, isAllBranchesSelected])

	return (
		<GlobalSidebar>
			<div className="max-h-full">
				<div className="flex flex-col space-y-2">
					{allowSelectAllBranches && (
						<div className="flex flex-row items-center justify-between">
							<span className="text-md font-medium tracking-wider text-gray-400">Branches</span>
							<AntdSwitch
								checked={isAllBranchesSelected}
								checkedChildren="All"
								unCheckedChildren="Selected"
								onChange={handleIsAllBranchesSelected}
							/>
						</div>
					)}

					<div className="flex flex-row items-center justify-between">
						<span className="text-md font-medium tracking-wider text-gray-400">SITES</span>
						<AntdSwitch
							checked={shouldShowActiveOnly}
							checkedChildren="Active"
							unCheckedChildren="Inactive"
							onChange={value => {
								handleIsActiveOnlySelected(value)
								selectActiveOnly(value)
								if (paramLocationBranch)
									dispatch(
										getLocationsByBranchId(paramLocationBranch, value, () => {
											if (!value)
												dispatch(
													setAllLocationsByBranchLoadedState({
														branchId: paramLocationBranch,
														isAllLoaded: true,
													}),
												)
										}),
									)
							}}
						/>
					</div>

					<AntdSelect1
						loading={loadingBranches}
						onChange={value => {
							if (typeof value === "string") {
								dispatch(getLocationsByBranchId(value, shouldShowActiveOnly))
								selectLocationBranch(value) // Using Semantic Action
							}
						}}
						value={paramLocationBranch}
						placeholder="Select branch"
						options={branches.map(({ _id, name }) => ({ label: name, value: _id }))}
						suffixIcon={
							<>
								<span style={{ color: "rgb(156 163 175)" }}>Branches</span>
								<CaretDownOutlined style={{ color: "rgb(156 163 175)" }} />
							</>
						}
						disabled={isAllBranchesSelected}
					/>
					<AntdSelect1
						loading={loadingCustomers}
						onChange={value => {
							selectCustomer(value) // Using Semantic Action
						}}
						value={paramCustomer}
						options={[{ label: "All Customers", value: "all" }, ...customers].map(({ value, label }) => ({
							label,
							value,
						}))}
						suffixIcon={
							<>
								<span style={{ color: "rgb(156 163 175)" }}>Customers</span>
								<CaretDownOutlined style={{ color: "rgb(156 163 175)" }} />
							</>
						}
						disabled={isAllBranchesSelected}
					/>
					<AntdInput
						id="sidebar-search"
						onChange={e => setSearchString(e.target.value)}
						value={searchString}
						placeholder="Search"
						type="text"
						suffix={
							<>
								<span style={{ color: "rgb(156 163 175)" }}>Ctrl + G</span>
								<SearchOutlined size={14} style={{ color: "rgb(156 163 175)" }} />{" "}
							</>
						}
						disabled={isAllBranchesSelected}
					/>
				</div>
				<Divider className="bg-slate-700" style={{ margin: "15px 0 10px 0" }} />
				<SidebarList
					loading={loading}
					selected={selected}
					setSelected={handleSelected}
					disableClick={forAllLocations || isAllBranchesSelected}
					list={filteredLocationList?.map((item: GetLocationDetailsInterface) => ({
						_id: item._id,
						name: item.shortName || item.name,
						isActive: item.isActive ?? true,
					}))}
				/>
			</div>
		</GlobalSidebar>
	)
}

export default LocationSidebar
